import _ from 'lodash'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { FormProvider, useFormContext } from 'react-hook-form'
import { useLingui } from '@lingui/react'
import { t, Trans } from '@lingui/macro'

import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material'
import CachedRoundedIcon from '@mui/icons-material/CachedRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'

import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import { AlertsPortal } from '@/components/ui/alert/AlertsPortal'
import { defaultLpsInfos, getInsIdentityResultMessage } from '@/constants/dmp-connect'
import { useLazyGetInsFromVitaleCardQuery } from '@/core/services/dmp-connect-js2'
import { InsIdentity, LpsInfos, VitaleCardPatient } from '@/core/types/dmp-connect'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { replaceAlerts } from '@/redux/reducers/alerts.reducer'
import {
  selectDMPConnectTlsiConnectorBillingNumber,
  selectDMPConnectTlsiConnectorId,
  selectVitaleCard as selectVitaleCardSelector,
} from '@/redux/reducers/dmp-connect.reducer'
import { getApiErrorMessage } from '@/utils/errors'
import { getVitalePatientQualityLabel } from '@/utils/patient'

import { DMPConnectVitaleCardFormData, useDMPConnectVitaleCardForm } from '../model/useDMPConnectVitaleCardForm'
import { DMPConnectVitaleCardPatient } from '../model/types'
import { useGetVitaleCards } from '../model/useGetVitaleCards'

export type DMPConnectVitaleCardFormProps = {
  hideGetIns?: boolean
  onSelectPatient?: (patient: DMPConnectVitaleCardPatient) => void
}

const DMP_CONNECT_VITALE_CARD_FORM_ALERTS = 'DMP_CONNECT_VITALE_CARD_FORM_ALERTS'

export function DMPConnectVitaleCardForm(props: DMPConnectVitaleCardFormProps) {
  const { hideGetIns, onSelectPatient } = props

  const vitaleCard = useAppSelector(selectVitaleCardSelector)

  const [patientInsMap, setPatientInsMap] = useState<Record<number, InsIdentity>>({})
  const [selectedPatientIndex, setSelectedPatientIndex] = useState<number>()

  const { isSameCard, isSubmitting, newCardInserted, noCardInserted, rhForm, submitHandler, vitaleCardPatients } = useDMPConnectVitaleCardForm()

  const vitaleCardPatientOptions = useMemo(() => {
    if ((newCardInserted && !isSameCard) || noCardInserted) return []
    return (
      (vitaleCardPatients ?? vitaleCard?.Patients)?.map((patient, index) => ({
        label: <SelectPatientOptionLabel hideGetIns={hideGetIns} index={index} patient={patient} setPatientInsMap={setPatientInsMap} />,
        value: index,
      })) ?? []
    )
  }, [vitaleCard, vitaleCardPatients, newCardInserted, noCardInserted, isSameCard, hideGetIns])

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(submitHandler)}>
        <Stack justifyContent="center" spacing={3}>
          {newCardInserted && !isSameCard && (
            <Alert severity="warning">
              <Trans>The inserted card has been changed</Trans>
            </Alert>
          )}
          {noCardInserted && (
            <Alert severity="warning">
              <Trans>No card inserted</Trans>
            </Alert>
          )}
          <div className="flex flex-col gap-4">
            <SelectCardReader disabled={noCardInserted} />
            <div className="flex justify-center">
              <LoadingButton loading={isSubmitting} disabled={noCardInserted} variant="contained" type="submit">
                <Trans>Read vitale card</Trans>
              </LoadingButton>
            </div>
          </div>
          {!!vitaleCardPatientOptions?.length && !isSubmitting && (
            <div className="flex flex-col gap-2">
              <RadioGroupInput
                onChange={(event) => {
                  setSelectedPatientIndex(Number(event.target.value))
                }}
                fullWidth
                options={vitaleCardPatientOptions}
                value={selectedPatientIndex?.toString() ?? ''}
                variant="contained"
                sx={{ '& .MuiFormControlLabel-label': { flex: 1 } }}
              />
              <div>
                <AlertsPortal area={DMP_CONNECT_VITALE_CARD_FORM_ALERTS} />
              </div>
              <Stack direction="row" justifyContent="center">
                <Button
                  onClick={() => {
                    if (selectedPatientIndex !== undefined && vitaleCardPatients?.[selectedPatientIndex]) {
                      onSelectPatient?.({ ...vitaleCardPatients[selectedPatientIndex], insIdentity: patientInsMap[selectedPatientIndex] ?? null })
                    }
                  }}
                  variant="contained"
                >
                  <Trans>Select</Trans>
                </Button>
              </Stack>
            </div>
          )}
        </Stack>
      </form>
    </FormProvider>
  )
}

function SelectCardReader({ disabled, newCardInserted }: { disabled?: boolean; newCardInserted?: boolean }) {
  useLingui()
  const rhForm = useFormContext<DMPConnectVitaleCardFormData>()

  const { isLoading, refetch, vitaleCards } = useGetVitaleCards()

  useEffect(() => {
    if (newCardInserted) {
      refetch()
    }
  }, [newCardInserted, refetch])

  return (
    <Box width="100%">
      <Box mt={1} width="100%" position="relative">
        <Tooltip placement="top" title={t`Refresh`}>
          <IconButton onClick={refetch} size="small" sx={{ position: 'absolute', top: '-2px', right: 0, zIndex: 1 }}>
            <CachedRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <SelectInputController
          control={rhForm.control}
          disabled={disabled}
          fullWidth
          label={t`Vitale card reader`}
          name="cardName"
          options={isLoading ? [] : vitaleCards}
          readOnly={isLoading}
        />
        {isLoading && (
          <Box position="absolute" top="75%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

function SelectPatientOptionLabel({
  hideGetIns,
  index,
  patient,
  setPatientInsMap,
}: {
  hideGetIns?: boolean
  index: number
  patient: VitaleCardPatient
  setPatientInsMap: Dispatch<SetStateAction<Record<number, InsIdentity>>>
}) {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const billingNumber = useAppSelector(selectDMPConnectTlsiConnectorBillingNumber)
  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  const [getInsFromVitaleCard, { data, isLoading }] = useLazyGetInsFromVitaleCardQuery()

  const handleGetIns = async () => {
    if (!tlsiConnectorId) return
    try {
      const LpsInfos: LpsInfos = { ...defaultLpsInfos, s_instance: tlsiConnectorId, s_billingNumber: billingNumber }
      const { i_insIdentityResult, Identity } = await getInsFromVitaleCard({ i_vitalePatientIndex: index, LpsInfos }).unwrap()
      if (i_insIdentityResult !== 2) {
        dispatch(
          replaceAlerts({ area: DMP_CONNECT_VITALE_CARD_FORM_ALERTS, alerts: [{ message: getInsIdentityResultMessage(i_insIdentityResult), type: 'error' }] })
        )
      }
      if (Identity) setPatientInsMap((prevPatientInsMap) => ({ ...prevPatientInsMap, [index]: Identity }))
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`Get INS failed due to unknown error`), { variant: 'error' })
    }
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <span>{`${patient.s_name} - ${patient.s_given}`.trim()}</span>
        <Chip label={getVitalePatientQualityLabel(patient.i_quality)} size="small" />
        {!!data?.Identity && <Chip icon={<CheckCircleRoundedIcon />} label={t`INS`} size="small" />}
      </Stack>
      {!hideGetIns && !data && (
        <LoadingButton loading={isLoading} onClick={handleGetIns}>
          <Trans>Get INS</Trans>
        </LoadingButton>
      )}
    </Stack>
  )
}
